import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings as SettingsIcon, Cpu, Route, ToggleRight, ToggleLeft, Save, Check, Loader2 } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';
import { pageTransition, fadeUpItem, staggerContainer } from '../hooks/useAnimation';

const API = '/api/agents';
interface ModelOption { id: string; label: string; note: string; }

function Toggle({ on, onChange, label, hint }: { on: boolean; onChange: (v: boolean) => void; label: string; hint: string }) {
  return (
    <button onClick={() => onChange(!on)} className="w-full flex items-center justify-between py-2 group">
      <div className="text-left">
        <p className="text-sm text-text-primary">{label}</p>
        <p className="text-[11px] text-text-muted">{hint}</p>
      </div>
      {on ? <ToggleRight className="w-7 h-7 text-success" /> : <ToggleLeft className="w-7 h-7 text-text-muted" />}
    </button>
  );
}

export function Settings() {
  const [models, setModels] = useState<ModelOption[]>([]);
  const [model, setModel] = useState('');
  const [defaultModel, setDefaultModel] = useState('');
  const [switching, setSwitching] = useState('');
  const [modelMsg, setModelMsg] = useState('');
  const [routing, setRouting] = useState<string>('rules');
  const [agentReasoning, setAgentReasoning] = useState(true);
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(1.0);
  const [topK, setTopK] = useState(0);
  const [paramMsg, setParamMsg] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/models`);
        if (r.ok) {
          const d = await r.json();
          setModels(d.options || []); setModel(d.current || ''); setDefaultModel(d.default || '');
        }
        const p = await fetch(`${API}/llm/params`);
        if (p.ok) {
          const d = await p.json();
          setTemperature(d.temperature ?? 0.7); setTopP(d.top_p ?? 1.0); setTopK(d.top_k ?? 0);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  // Apply sampling params to the live LLM (called on slider release).
  const applyParams = async (patch: Record<string, number>) => {
    try {
      const r = await fetch(`${API}/llm/params`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error();
      setParamMsg('Sampling updated — applies to new calls.');
      setTimeout(() => setParamMsg(''), 3000);
    } catch { setParamMsg('Could not update sampling.'); }
  };

  const chooseModel = async (id: string) => {
    if (id === model || switching) return;
    setSwitching(id); setModelMsg('');
    try {
      const r = await fetch(`${API}/model`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ model: id }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setModel(d.current);
      setModelMsg(`Now using ${d.current} — applies to new calls immediately.`);
      setTimeout(() => setModelMsg(''), 4000);
    } catch {
      setModelMsg('Could not switch model.');
    } finally { setSwitching(''); }
  };

  const save = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <motion.div variants={pageTransition} initial="initial" animate="animate" exit="exit" className="p-6 space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
          <SettingsIcon className="w-6 h-6 text-vokentra" />
          Settings
        </h1>
        <p className="text-sm text-text-muted mt-1">Agent and LLM configuration (mirrors backend .env)</p>
      </div>

      <motion.div variants={staggerContainer(0.08)} initial="hidden" animate="visible" className="space-y-4">
        {/* LLM model */}
        <motion.div variants={fadeUpItem}>
          <GlassCard padding="md">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-vokentra" />
                <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">LLM Model</h2>
              </div>
              {model && <span className="text-[11px] text-text-muted">active: <span className="text-text-primary font-mono">{model}</span></span>}
            </div>
            <div className="space-y-2">
              {models.map((m) => {
                const active = model === m.id;
                const busy = switching === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => chooseModel(m.id)}
                    disabled={!!switching}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-left transition-colors disabled:opacity-60 ${
                      active ? 'bg-vokentra/15 border-vokentra/40' : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-text-primary font-medium">{m.label}</span>
                        <span className="text-[10px] text-text-muted font-mono">{m.id}</span>
                        {m.id === defaultModel && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.06] text-text-muted">default</span>}
                      </div>
                      <p className="text-[11px] text-text-muted">{m.note}</p>
                    </div>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin text-vokentra shrink-0" />
                          : active ? <Check className="w-4 h-4 text-vokentra shrink-0" /> : null}
                  </button>
                );
              })}
              {models.length === 0 && <p className="text-sm text-text-muted">Loading models…</p>}
            </div>
            {modelMsg && <p className="text-[11px] text-success mt-2">{modelMsg}</p>}
            <p className="text-[11px] text-text-muted mt-2">
              Switching applies immediately to new calls. It resets to the <span className="font-mono">{defaultModel || 'default'}</span>{' '}
              on restart — set <span className="font-mono">OLLAMA_MODEL</span> in <span className="font-mono">.env</span> to make it permanent.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-[11px] text-text-muted">Temperature: <span className="text-text-secondary tabular-nums">{temperature.toFixed(2)}</span> <span className="text-text-muted">· randomness (low = focused, high = creative)</span></label>
                <input type="range" min={0} max={2} step={0.05} value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  onPointerUp={(e) => applyParams({ temperature: parseFloat((e.target as HTMLInputElement).value) })}
                  onKeyUp={(e) => applyParams({ temperature: parseFloat((e.target as HTMLInputElement).value) })}
                  className="w-full accent-vokentra mt-1" />
              </div>
              <div>
                <label className="text-[11px] text-text-muted">top_p: <span className="text-text-secondary tabular-nums">{topP.toFixed(2)}</span> <span className="text-text-muted">· nucleus sampling (1.0 = off)</span></label>
                <input type="range" min={0.1} max={1} step={0.05} value={topP}
                  onChange={(e) => setTopP(parseFloat(e.target.value))}
                  onPointerUp={(e) => applyParams({ top_p: parseFloat((e.target as HTMLInputElement).value) })}
                  onKeyUp={(e) => applyParams({ top_p: parseFloat((e.target as HTMLInputElement).value) })}
                  className="w-full accent-vokentra mt-1" />
              </div>
              <div>
                <label className="text-[11px] text-text-muted">top_k: <span className="text-text-secondary tabular-nums">{topK}</span> <span className="text-text-muted">· 0 = off (model default)</span></label>
                <input type="range" min={0} max={100} step={1} value={topK}
                  onChange={(e) => setTopK(parseInt(e.target.value))}
                  onPointerUp={(e) => applyParams({ top_k: parseInt((e.target as HTMLInputElement).value) })}
                  onKeyUp={(e) => applyParams({ top_k: parseInt((e.target as HTMLInputElement).value) })}
                  className="w-full accent-vokentra mt-1" />
              </div>
              {paramMsg && <p className="text-[11px] text-success">{paramMsg}</p>}
            </div>
          </GlassCard>
        </motion.div>

        {/* Routing */}
        <motion.div variants={fadeUpItem}>
          <GlassCard padding="md">
            <div className="flex items-center gap-2 mb-3">
              <Route className="w-4 h-4 text-info" />
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">Orchestration Routing</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { id: 'rules', label: 'Rule-based', note: 'fast · voice' },
                { id: 'llm', label: 'LLM-driven', note: 'autonomous' },
                { id: 'blackboard', label: 'Blackboard', note: 'shared board' },
                { id: 'swarm', label: 'Swarm', note: 'handoffs' },
                { id: 'sequential', label: 'Sequential', note: 'pipeline' },
                { id: 'parallel', label: 'Parallel', note: 'concurrent' },
              ].map((r) => (
                <button
                  key={r.id}
                  onClick={() => setRouting(r.id)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors text-left ${
                    routing === r.id ? 'bg-info/15 border-info/40 text-text-primary' : 'bg-white/[0.02] border-white/[0.06] text-text-muted hover:text-text-secondary'
                  }`}
                >
                  <div>{r.label}</div>
                  <div className="text-[10px] text-text-muted">{r.note}</div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-text-muted mt-2">
              Used by the multi-agent <span className="font-mono">/converse</span> path. Blackboard and
              Swarm are advanced multi-agent coordination patterns.
            </p>
          </GlassCard>
        </motion.div>

        {/* Toggles */}
        <motion.div variants={fadeUpItem}>
          <GlassCard padding="md">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">Agent Behaviour</h2>
            </div>
            <Toggle
              on={agentReasoning}
              onChange={setAgentReasoning}
              label="Per-agent LLM reasoning"
              hint="AGENT_LLM_REASONING — each agent reasons via the LLM (rule-based fallback)"
            />
          </GlassCard>
        </motion.div>

        {/* Save */}
        <motion.div variants={fadeUpItem} className="flex items-center gap-3">
          <button
            onClick={save}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-vokentra text-white text-sm font-medium hover:bg-vokentra-dark transition-colors"
          >
            <Save className="w-4 h-4" /> Save changes
          </button>
          {saved && <span className="text-xs text-success">Saved (demo — not persisted)</span>}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export default Settings;
